<template>
  <div class="rounded-medium flex flex-col">
    <h3
      class="text-title-small mb-2"
      :class="[!error && 'text-on-surface-variant', error && 'text-error']"
    >
      {{ $t('startSurvey.selectTemplate') }}
    </h3>
    <p v-if="error" class="text-error text-body-small -mt-2 mb-1">{{ error }}</p>

    <ul class="flex w-full flex-col gap-2">
      <li v-for="template in templates" :key="template.id">
        <SelectableAndExpandableTopicListItem
          :feedback-topic="template"
          :supporting-text="
            isDisabled(template) ? $t('startSurvey.noSharedPerspectivesTooltip') : undefined
          "
          :selected="isSelected(template.id)"
          :disabled="isDisabled(template)"
          :expanded="expandedTemplateId === template.id"
          :single-choice="singleChoice"
          @toggle-expanded="toggleExpandedTemplateId(template.id)"
          @toggle-selected="toggleFeedbackTemplateIds(template.id)"
        />
      </li>
    </ul>
    <FeedbackTopicDetailsDialog
      :is-open="isDetailsOpen"
      :topic="detailsTopic"
      @close="closeTopicDetails"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue'
  import type { StructuredFeedbackTemplate } from '../../../modules/survey/models/structured-feedback-template'
  import FeedbackTopicDetailsDialog from '@/core/components/feedback-topic/FeedbackTopicDetailsDialog.vue'
  import { intersectNonSelfPerspectiveKeys } from '@/core/models/perspective'
  import SelectableAndExpandableTopicListItem from '../feedback-topic/SelectableAndExpandableTopicListItem.vue'

  interface Props {
    templates: StructuredFeedbackTemplate[]
    showPerspectives?: boolean
    requireSharedPerspectives?: boolean
    error?: string
    singleChoice?: boolean
  }

  const props = defineProps<Props>()

  const expandedTemplateId = ref<string | undefined>(undefined)

  const templateIds = defineModel<string[] | string | undefined>({ required: true })

  const isDetailsOpen = ref(false)
  const detailsTopic = ref<StructuredFeedbackTemplate | null>(null)

  const closeTopicDetails = () => {
    isDetailsOpen.value = false
  }

  const isDisabled = (template: StructuredFeedbackTemplate) => {
    if (!props.requireSharedPerspectives) return false
    const selectedIds = templateIds.value
    if (!Array.isArray(selectedIds) || selectedIds.length === 0) return false
    if (selectedIds.includes(template.id)) return false

    const selectedTemplates = props.templates.filter((item) => selectedIds.includes(item.id))
    return intersectNonSelfPerspectiveKeys([...selectedTemplates, template]).size === 0
  }

  const toggleFeedbackTemplateIds = (feedbackTemplateId: string) => {
    const template = props.templates.find((item) => item.id === feedbackTemplateId)
    if (template && isDisabled(template)) return

    if (Array.isArray(templateIds.value)) {
      if (templateIds.value.find((id) => id == feedbackTemplateId)) {
        templateIds.value = templateIds.value.filter((id) => id != feedbackTemplateId)
      } else {
        templateIds.value.push(feedbackTemplateId)
      }
    } else {
      templateIds.value = feedbackTemplateId
    }
  }

  const isSelected = (templateId: string) => {
    if (Array.isArray(templateIds.value)) {
      return !!templateIds.value.find((id) => id == templateId)
    } else {
      return templateIds.value == templateId
    }
  }

  const toggleExpandedTemplateId = (templateId: string) => {
    if (expandedTemplateId.value === templateId) {
      expandedTemplateId.value = undefined
    } else {
      expandedTemplateId.value = templateId
    }
  }
</script>
